/**
 * In-app notification provider.
 *
 * Shows a dark semi-transparent toast banner that slides down from the top
 * when something happens (e.g. onboarding item approved/rejected).
 *
 * Usage:
 *   const { showNotification } = useNotification();
 *   showNotification("TB Test — Approved", "approved");
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
    Animated,
    Dimensions,
    Easing,
    Platform,
    StyleSheet,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { colors, fonts, radius, shadows, spacing } from "./theme";

type NotificationStatus = "approved" | "rejected" | "failed" | "info";

interface Notification {
    message: string;
    status: NotificationStatus;
    onPress?: () => void;
}

interface NotificationContextValue {
    showNotification: (message: string, status: NotificationStatus, onPress?: () => void) => void;
}

const NotificationContext = createContext<NotificationContextValue>({
    showNotification: () => {}
});

export function useNotification(): NotificationContextValue {
    return useContext(NotificationContext);
}

const DISMISS_DELAY = 4000;
const ANIM_DURATION = 300;
const SCREEN_WIDTH = Dimensions.get("window").width;

const STATUS_COLORS: Record<NotificationStatus, string> = {
    approved: colors.success500,
    rejected: colors.error500,
    failed: colors.warning500,
    info: colors.teal400
};

// ── Pinwheel logo (small) ──────────────────────────────────────
function MiniPinwheel() {
    const sz = 24;
    const half = sz / 2;
    return (
        <View style={{ width: sz, height: sz }}>
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
        </View>
    );
}

// ── Toast banner ───────────────────────────────────────────────
function InAppNotification({
    notification,
    topInset,
    onDismiss
}: {
    notification: Notification;
    topInset: number;
    onDismiss: () => void;
}) {
    const translateY = useRef(new Animated.Value(-120)).current;
    const opacity = useRef(new Animated.Value(0)).current;
    const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        // Slide in
        Animated.parallel([
            Animated.timing(translateY, {
                toValue: 0,
                duration: ANIM_DURATION,
                easing: Easing.out(Easing.cubic),
                useNativeDriver: Platform.OS !== "web"
            }),
            Animated.timing(opacity, {
                toValue: 1,
                duration: ANIM_DURATION,
                useNativeDriver: Platform.OS !== "web"
            })
        ]).start();

        // Auto-dismiss
        dismissTimer.current = setTimeout(dismiss, DISMISS_DELAY);

        return () => {
            if (dismissTimer.current) {
                clearTimeout(dismissTimer.current);
            }
        };
    }, []);

    function dismiss() {
        if (dismissTimer.current) {
            clearTimeout(dismissTimer.current);
        }
        Animated.parallel([
            Animated.timing(translateY, {
                toValue: -120,
                duration: ANIM_DURATION,
                easing: Easing.in(Easing.cubic),
                useNativeDriver: Platform.OS !== "web"
            }),
            Animated.timing(opacity, {
                toValue: 0,
                duration: ANIM_DURATION,
                useNativeDriver: Platform.OS !== "web"
            })
        ]).start(() => onDismiss());
    }

    const statusColor = STATUS_COLORS[notification.status];

    return (
        <Animated.View
            style={[
                styles.banner,
                {
                    top: topInset + 8,
                    transform: [{ translateY }],
                    opacity
                }
            ]}
        >
            <TouchableOpacity
                style={styles.bannerInner}
                activeOpacity={0.9}
                onPress={() => {
                    if (notification.onPress) {
                        notification.onPress();
                    }
                    dismiss();
                }}
            >
                <MiniPinwheel />
                <View style={styles.bannerContent}>
                    <Text style={styles.bannerMessage} numberOfLines={2}>
                        {notification.message}
                    </Text>
                    <Text style={styles.bannerSubtitle}>Task Health</Text>
                </View>
                <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
            </TouchableOpacity>
        </Animated.View>
    );
}

// ── Provider ───────────────────────────────────────────────────
export function NotificationProvider({ children }: { children: React.ReactNode }) {
    const [current, setCurrent] = useState<Notification | null>(null);
    const insets = useSafeAreaInsets();
    const keyRef = useRef(0);
    const [key, setKey] = useState(0);

    const showNotification = useCallback(
        (message: string, status: NotificationStatus, onPress?: () => void) => {
            keyRef.current += 1;
            setKey(keyRef.current);
            setCurrent({ message, status, onPress });
        },
        []
    );

    const value = React.useMemo(() => ({ showNotification }), [showNotification]);

    return (
        <NotificationContext.Provider value={value}>
            {children}
            {current && (
                <InAppNotification
                    key={key}
                    notification={current}
                    topInset={insets.top}
                    onDismiss={() => setCurrent(null)}
                />
            )}
        </NotificationContext.Provider>
    );
}

const styles = StyleSheet.create({
    banner: {
        position: "absolute",
        left: spacing.base,
        right: spacing.base,
        zIndex: 9999,
        ...shadows.lg
    },
    bannerInner: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        backgroundColor: "rgba(31, 21, 71, 0.92)",
        borderRadius: radius.lg,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.base
    },
    bannerContent: {
        flex: 1,
        gap: 2
    },
    bannerMessage: {
        fontSize: 14,
        fontFamily: fonts.semiBold,
        color: colors.surface
    },
    bannerSubtitle: {
        fontSize: 11,
        fontFamily: fonts.regular,
        color: "rgba(255, 255, 255, 0.5)"
    },
    statusDot: {
        width: 10,
        height: 10,
        borderRadius: 5
    }
});
